import { useAtomValue } from 'jotai';
import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DraggableCore, DraggableData, DraggableEvent } from 'react-draggable';
import { SidePanelButton } from 'src/components/Buttons/SidePanelButton';
import { userSettingsFamilyAtom } from 'src/contexts/SellerUserSettingsContext/SellerUserAllSettingsContext.atom';
import {
  SidePanelPage,
  useSidePanelContext,
} from 'src/contexts/SidePanelContext';
import { useComputedStateFirstNotNull } from 'src/hooks/useComputedState';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { Feature, UserSetting } from 'src/WebApiController';

import {
  ContainerStyledDiv,
  ContentStyledDiv,
  DividerContainer,
} from './ResizablePanel.styled';

const MIN_PANEL_SIZE = 120; // enough to show grouping info
const MAX_PANEL_SIZE = 420;

export enum ResizeEdge {
  North,
  South,
  East,
  West,
}

export type ResizablePanelProps = {
  direction: ResizeEdge;
  sidePanelPage?: SidePanelPage;
  initialSize?: number;
  disabled?: boolean;
  onResize?: (curSize: number) => void;
  autoHide?: boolean;
  minPanelSize?: number;
  maxPanelSize?: number;
  minPanelPercent?: number;
  maxPanelPercent?: number;
  defaultRatio?: number;
  showExpandButton?: boolean;
};

export function ResizablePanel({
  children,
  direction,
  initialSize,
  disabled,
  onResize,
  sidePanelPage,
  autoHide = true,
  minPanelSize = MIN_PANEL_SIZE,
  maxPanelSize = MAX_PANEL_SIZE,
  minPanelPercent,
  maxPanelPercent,
  defaultRatio = 0.25,
  showExpandButton,
}: PropsWithChildren<ResizablePanelProps>) {
  const hasReworkUserSettingsContextFeature = useUserHasFeature(
    Feature.FEPerf_ReworkUserSettingsContext
  );
  const {
    isHidden,
    setCollapsed,
    setHidden,
    sidePanelWidths,
    onUpdateSidePanelWidth,
  } = useSidePanelContext();
  const userSettingValue = useAtomValue(
    userSettingsFamilyAtom(UserSetting.SidePanelWidths)
  );
  const sanitizedSidePanelWidths = useMemo(() => {
    if (!hasReworkUserSettingsContextFeature) {
      return sidePanelWidths;
    }
    if (userSettingValue) {
      return JSON.parse(userSettingValue) satisfies Record<
        SidePanelPage,
        number
      >;
    }
    return undefined;
  }, [hasReworkUserSettingsContextFeature, sidePanelWidths, userSettingValue]);
  const [disableAnimation, setDisabledAnimation] = useState(false);

  const isHorizontal =
    direction === ResizeEdge.East || direction === ResizeEdge.West;

  const contentRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const lastHandleRect = useRef<DOMRect | null>(null);
  const currentSizeRef = useRef<number>(0);

  const getMaxSize = useCallback(() => {
    const parentBound =
      wrapperRef.current?.parentElement?.getBoundingClientRect();

    const parentSize = isHorizontal ? parentBound?.width : parentBound?.height;
    return maxPanelPercent && parentSize
      ? maxPanelPercent * parentSize
      : maxPanelSize;
  }, [isHorizontal, maxPanelPercent, maxPanelSize]);

  // Memoized user settings on the panel width
  const userSettingPanelWidth = useMemo(() => {
    if (sidePanelPage && sanitizedSidePanelWidths?.[sidePanelPage]) {
      return sanitizedSidePanelWidths[sidePanelPage];
    }
    return undefined;
  }, [sidePanelPage, sanitizedSidePanelWidths]);

  // Custom state hook, which will be default to the max size
  // and initialzed with the first panel with from server setting
  const [size, setSize] = useComputedStateFirstNotNull(
    isHidden ? 0 : userSettingPanelWidth,
    getMaxSize()
  );

  const getMinSize = useCallback(() => {
    const parentBound =
      wrapperRef.current?.parentElement?.getBoundingClientRect();
    const parentSize = isHorizontal ? parentBound?.width : parentBound?.height;
    return minPanelPercent && parentSize
      ? minPanelPercent * parentSize
      : minPanelSize;
  }, [isHorizontal, minPanelPercent, minPanelSize]);

  const updateSize = useCallback(
    (size: number, updateSettings = true) => {
      if (isHidden) {
        return;
      }
      const minSize = getMinSize();
      const maxSize = getMaxSize();
      let actualSize = size;
      if (actualSize < minSize) {
        actualSize = minSize;
      }
      actualSize = Math.max(minSize, actualSize);
      actualSize = Math.min(actualSize, maxSize);
      setCollapsed(actualSize <= minSize);
      if (actualSize <= minSize && autoHide) {
        setHidden(true);
      } else {
        currentSizeRef.current = actualSize;
        setSize(actualSize);
        onResize?.(actualSize);
        if (sidePanelPage && updateSettings) {
          onUpdateSidePanelWidth(sidePanelPage, actualSize);
        }
      }
    },
    [
      isHidden,
      getMinSize,
      getMaxSize,
      setCollapsed,
      autoHide,
      setHidden,
      setSize,
      onResize,
      sidePanelPage,
      onUpdateSidePanelWidth,
    ]
  );

  // Important: trick to avoid using userSettingPanelWidth as dependency
  // but still want to use it in the side effect function.
  const userSettingPanelWidthRef = useRef<number | undefined>();
  userSettingPanelWidthRef.current = userSettingPanelWidth;

  useEffect(() => {
    const content = contentRef.current;
    const parent = wrapperRef.current?.parentElement;
    if (!content || !parent) {
      return;
    }

    const actualContent = content.children[0];
    if (userSettingPanelWidthRef.current) {
      currentSizeRef.current = userSettingPanelWidthRef.current;
      setSize(userSettingPanelWidthRef.current);
    } else if (initialSize != null) {
      updateSize(initialSize, false);
    } else if (size === 0) {
      const bound = actualContent.getBoundingClientRect();
      // !!Important, assigned the current rect bound to record the last visited position
      lastHandleRect.current = bound;
      const parentBound =
        wrapperRef.current?.parentElement?.getBoundingClientRect();
      const parentSize = isHorizontal
        ? parentBound?.width
        : parentBound?.height;

      let proposedSize = isHorizontal ? bound.width : bound.height;
      proposedSize = proposedSize || defaultRatio * parentSize;

      // Initialize the size value based on the content's current size
      updateSize(proposedSize, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSize]);

  // Side effect to reset the size when user hide/open the side panel
  // FIXME: Ideally this should be a callback, rather than a side effect to make is safe.
  useEffect(() => {
    if (isHidden) {
      currentSizeRef.current = 0;
      setSize(0);
      onResize?.(0);
      return;
    }
    const defaultSize = userSettingPanelWidthRef.current ?? getMaxSize();
    currentSizeRef.current = defaultSize;
    setSize(defaultSize);
    onResize?.(defaultSize);
    setCollapsed(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHidden]);

  const onDrag = useCallback(
    (_: DraggableEvent, data: DraggableData) => {
      const handleRect = contentRef.current?.getBoundingClientRect();
      if (!handleRect) {
        return;
      }
      let { deltaX, deltaY } = data;
      // Calculate the deltas from the relative positions between the current rect and the last handled rect
      if (lastHandleRect.current) {
        deltaX += handleRect.left - lastHandleRect.current.left;
        deltaY += handleRect.top - lastHandleRect.current.top;
      }
      // !!Importatnt, assigned the current rect bound to record the last visited position
      lastHandleRect.current = handleRect;
      // modify the size based on the drag delta
      const delta = isHorizontal ? deltaX : deltaY;

      const factor = [ResizeEdge.East, ResizeEdge.South].includes(direction)
        ? -1
        : 1;
      updateSize(Math.max(10, currentSizeRef.current - delta * factor));

      if (isHidden) {
        if (deltaX > 0 && direction === ResizeEdge.East) {
          setHidden(false);
        } else if (deltaX < 0 && direction === ResizeEdge.West) {
          setHidden(false);
        } else if (deltaY > 0 && direction === ResizeEdge.South) {
          setHidden(false);
        } else if (deltaY < 0 && direction === ResizeEdge.North) {
          setHidden(false);
        }
      }
    },
    [direction, isHidden, isHorizontal, setHidden, updateSize]
  );

  const divider = disabled ? null : (
    <DraggableCore
      onDrag={onDrag}
      onStart={() => setDisabledAnimation(true)}
      onStop={() => setDisabledAnimation(false)}
      disabled={disabled}
      grid={
        initialSize == null
          ? undefined
          : [isHorizontal ? initialSize : 0, !isHorizontal ? initialSize : 0]
      }
    >
      <DividerContainer isHorizontal={isHorizontal}>
        {showExpandButton && <SidePanelButton direction={direction} />}
        <div className="divider" />
      </DividerContainer>
    </DraggableCore>
  );
  const isHandleInFront =
    direction === ResizeEdge.West || direction === ResizeEdge.North;
  return (
    <ContainerStyledDiv ref={wrapperRef} isHorizontal={isHorizontal}>
      {isHandleInFront && divider}
      <ContentStyledDiv
        shouldAnimateContent={!disableAnimation}
        ref={contentRef}
        isHorizontal={isHorizontal}
        size={size}
      >
        {
          // this ensure that children is only 1 element
          React.Children.only(children)
        }
      </ContentStyledDiv>
      {!isHandleInFront && divider}
    </ContainerStyledDiv>
  );
}
